<template>
	<div class="lesson-card" :class="lessonTypeClass">
		<!-- Drag Handle -->
		<div
			class="lesson-card__drag-handle"
			title="Перетащите для изменения порядка"
		>
			<span>⋮⋮</span>
		</div>

		<!-- Content -->
		<div class="lesson-card__content">
			<!-- Header -->
			<div class="lesson-card__header">
				<div class="lesson-card__info">
					<div class="lesson-card__title-row">
						<span class="lesson-card__type-icon">{{ typeIcon }}</span>
						<h4 class="lesson-card__title">{{ lesson.title }}</h4>
					</div>
					<p v-if="lesson.description" class="lesson-card__description">
						{{ lesson.description }}
					</p>
				</div>

				<!-- Type Badge -->
				<div class="lesson-card__badges">
					<span class="lesson-card__badge" :class="typeBadgeClass">
						{{ typeLabel }}
					</span>
				</div>
			</div>

			<!-- Meta Info -->
			<div class="lesson-card__meta">
				<span class="lesson-card__meta-item">
					<span class="lesson-card__meta-icon">📚</span>
					Порядок: {{ lesson.order_number }}
				</span>
				<span v-if="lesson.duration_minutes" class="lesson-card__meta-item">
					<span class="lesson-card__meta-icon">⏱️</span>
					{{ lesson.duration_minutes }} мин
				</span>
				<span v-if="lesson.video_url" class="lesson-card__meta-item">
					<span class="lesson-card__meta-icon">🎥</span>
					Видео
				</span>
				<span v-if="lesson.materials?.length" class="lesson-card__meta-item">
					<span class="lesson-card__meta-icon">📎</span>
					{{ lesson.materials.length }} материалов
				</span>
			</div>

			<!-- Actions -->
			<div class="lesson-card__actions">
				<button
					class="lesson-card__action-btn lesson-card__action-btn--view"
					type="button"
					@click="$emit('view', lesson)"
					title="Просмотреть"
				>
					<span>👁️</span> Просмотр
				</button>
				<button
					class="lesson-card__action-btn lesson-card__action-btn--edit"
					type="button"
					@click="$emit('edit', lesson)"
					title="Редактировать"
				>
					<span>✏️</span> Редактировать
				</button>
				<button
					class="lesson-card__action-btn lesson-card__action-btn--delete"
					type="button"
					@click="handleDelete"
					title="Удалить"
				>
					<span>🗑️</span> Удалить
				</button>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'LessonCard',
	props: {
		lesson: {
			type: Object,
			required: true,
		},
	},
	computed: {
		lessonTypeClass() {
			return `lesson-card--${this.lesson.lesson_type || 'video'}`
		},
		typeIcon() {
			const icons = {
				video: '🎥',
				article: '📄',
			}
			return icons[this.lesson.lesson_type] || '📚'
		},
		typeLabel() {
			const labels = {
				video: 'Видео',
				article: 'Статья',
			}
			return labels[this.lesson.lesson_type] || 'Урок'
		},
		typeBadgeClass() {
			return `lesson-card__badge--${this.lesson.lesson_type || 'video'}`
		},
	},
	methods: {
		handleDelete() {
			if (
				confirm(
					`Вы уверены, что хотите удалить урок "${this.lesson.title}"? Это действие нельзя отменить.`
				)
			) {
				this.$emit('delete', this.lesson)
			}
		},
	},
}
</script>

<style scoped>
.lesson-card {
	display: flex;
	gap: 12px;
	background-color: #ffffff;
	border: 2px solid #e8e8e8;
	border-radius: 12px;
	padding: 20px;
	transition: all 0.3s ease;
}

.lesson-card:hover {
	border-color: #0095ec;
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.15);
}

.lesson-card--video {
	border-left: 4px solid #0095ec;
}

.lesson-card--article {
	border-left: 4px solid #28a745;
}

/* Drag Handle */
.lesson-card__drag-handle {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	color: #b0b0b0;
	font-size: 20px;
	cursor: grab;
	user-select: none;
}

.lesson-card__drag-handle:active {
	cursor: grabbing;
}

/* Content */
.lesson-card__content {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 16px;
}

/* Header */
.lesson-card__header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 16px;
}

.lesson-card__info {
	flex: 1;
}

.lesson-card__title-row {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
}

.lesson-card__type-icon {
	font-size: 20px;
}

.lesson-card__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 18px;
	font-weight: 600;
	margin: 0;
}

.lesson-card__description {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	line-height: 1.5;
	margin: 0;
}

/* Badges */
.lesson-card__badges {
	display: flex;
	gap: 8px;
}

.lesson-card__badge {
	padding: 4px 12px;
	border-radius: 12px;
	font-family: 'Poppins', Helvetica;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
}

.lesson-card__badge--video {
	background-color: #e3f2fd;
	color: #0095ec;
}

.lesson-card__badge--article {
	background-color: #e8f5e9;
	color: #28a745;
}

/* Meta */
.lesson-card__meta {
	display: flex;
	gap: 16px;
	flex-wrap: wrap;
}

.lesson-card__meta-item {
	display: flex;
	align-items: center;
	gap: 6px;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
}

.lesson-card__meta-icon {
	font-size: 16px;
}

/* Actions */
.lesson-card__actions {
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
}

.lesson-card__action-btn {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 8px 16px;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	background-color: #ffffff;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.lesson-card__action-btn:hover {
	transform: translateY(-2px);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.lesson-card__action-btn--view:hover {
	border-color: #0095ec;
	color: #0095ec;
}

.lesson-card__action-btn--edit:hover {
	border-color: #ffc107;
	color: #ffc107;
}

.lesson-card__action-btn--delete:hover {
	border-color: #dc3545;
	color: #dc3545;
	background-color: #fff5f5;
}
</style>

